import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Camera, Link2, Palette, Video } from "lucide-react";

// "Ihre A-Team Toolbox für Marketing" — compact create-shortcuts. Replaces the
// old ModuleCarousel (Rechnungsmanager / Invoice-Chat / Competitor-Tracker were
// removed as no longer valid, per spec §5); the four pillar how-to cards shrink
// to these shortcuts for returning users.

interface Shortcut {
  id: string;
  title: string;
  description: string;
  icon: typeof Camera;
  accent: "green" | "purple";
  disabled?: boolean;
  badge?: string;
}

const SHORTCUTS: Shortcut[] = [
  {
    id: "photo",
    title: "Foto → Post",
    description: "Foto hochladen, Ihr Team macht Beiträge für alle Kanäle",
    icon: Camera,
    accent: "green",
  },
  {
    id: "url",
    title: "YouTube/Artikel → Post",
    description: "Link einfügen, Inhalte werden zu Posts verarbeitet",
    icon: Link2,
    accent: "green",
  },
  {
    id: "visuals",
    title: "Brand Visuals",
    description: "Aus einem Foto werden 3 professionelle Brand-Visuals",
    icon: Palette,
    accent: "purple",
  },
  {
    id: "video",
    title: "Foto → Video",
    description: "Text, Voiceover und Animation in Sekunden",
    icon: Video,
    accent: "purple",
    disabled: true,
    badge: "Bald verfügbar",
  },
];

const ACCENTS = {
  green: {
    icon: "text-primary",
    iconBg: "bg-primary/10",
    border: "hover:border-primary/60",
  },
  purple: {
    icon: "text-purple-600 dark:text-purple-400",
    iconBg: "bg-purple-100 dark:bg-purple-900/30",
    border: "hover:border-purple-400",
  },
} as const;

interface ToolboxSectionProps {
  onShortcutClick: (id: "photo" | "url" | "visuals") => void;
  /** Shortcuts left out (Hotel-Edition: links, Brand Visuals and video are extra modules, PLAN §5.1 item 4). */
  hiddenIds?: readonly string[];
}

export function ToolboxSection({ onShortcutClick, hiddenIds = [] }: ToolboxSectionProps) {
  return (
    <section data-testid="toolbox-section">
      <h3 className="text-lg sm:text-xl font-semibold mb-3">Ihre A-Team Toolbox für Marketing</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {SHORTCUTS.filter((s) => !hiddenIds.includes(s.id)).map((s) => {
          const accent = ACCENTS[s.accent];
          const Icon = s.icon;
          return (
            <Card
              key={s.id}
              className={`relative transition-colors ${s.disabled ? "opacity-60" : `cursor-pointer ${accent.border}`}`}
              onClick={s.disabled ? undefined : () => onShortcutClick(s.id as "photo" | "url" | "visuals")}
              role={s.disabled ? undefined : "button"}
              tabIndex={s.disabled ? undefined : 0}
              onKeyDown={
                s.disabled
                  ? undefined
                  : (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onShortcutClick(s.id as "photo" | "url" | "visuals");
                      }
                    }
              }
              aria-disabled={s.disabled}
              aria-label={s.title}
              data-testid={`shortcut-${s.id}`}
            >
              <CardContent className="p-4">
                {s.badge && (
                  <Badge className="absolute top-2 right-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200 text-[10px]">
                    {s.badge}
                  </Badge>
                )}
                <div className={`w-9 h-9 rounded-lg ${accent.iconBg} flex items-center justify-center mb-2.5`}>
                  <Icon className={`w-5 h-5 ${accent.icon}`} aria-hidden="true" />
                </div>
                <p className="text-sm font-semibold leading-tight">{s.title}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">{s.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
